import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useNotify } from "@/hooks/useNotify";
import AdvancedLogoUpload from "@/components/ui/advanced-logo-upload";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Building, Mail, Phone, Globe, MapPin, Lock, CreditCard } from "lucide-react";
import { useImpersonationGuard } from "@/hooks/useImpersonationGuard";

const CompanySettings = () => {
  const { guardWrite } = useImpersonationGuard();
  const [isLoading, setIsLoading] = useState(false);
  
  // Company data
  const [companyName, setCompanyName] = useState("");
  const [companyPhoneNumber, setCompanyPhoneNumber] = useState("");
  const [website, setWebsite] = useState("");
  const [email, setEmail] = useState("");
  const [companyStreetAddress, setCompanyStreetAddress] = useState("");
  const [companySuburb, setCompanySuburb] = useState("");
  const [companyCity, setCompanyCity] = useState("");
  const [companyProvinceState, setCompanyProvinceState] = useState("");
  const [companyPostalCode, setCompanyPostalCode] = useState("");
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isVatRegistered, setIsVatRegistered] = useState(false);
  const [vatNumber, setVatNumber] = useState("");
  const [vatRate, setVatRate] = useState<number>(15);

  // Banking information state
  const [useBankInfo, setUseBankInfo] = useState(false);
  const [bankName, setBankName] = useState("");
  const [branchName, setBranchName] = useState("");
  const [branchCode, setBranchCode] = useState("");
  const [accountHolder, setAccountHolder] = useState("");
  const [accountNumber, setAccountNumber] = useState("");

  const notify = useNotify();
  const { user, profile, isAdmin: isCompanyAdmin } = useAuth();

  const fetchCompanyData = async () => {
    if (!user?.id) return;

    try {
      // Get user's company_id first
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;

      if (profileData?.company_id) {
        const { data: companyData, error: companyError } = await supabase
          .from('companies')
          .select(`
            name,
            email,
            website,
            street_address,
            suburb,
            city,
            province_state,
            postal_code,
            company_phone_number,
            logo_url,
            is_vat_registered,
            vat_number,
            "VAT Rate",
            "Use Bank Info",
            "Bank Name",
            "Branch Name",
            "Branch Code",
            "Account Holder",
            "Account Number"
          `)
          .eq('id', profileData.company_id)
          .single();

        if (companyError) throw companyError;

        if (companyData) {
          setCompanyName(companyData.name || "");
          setEmail(companyData.email || "");
          setWebsite(companyData.website || "");
          setCompanyStreetAddress(companyData.street_address || "");
          setCompanySuburb(companyData.suburb || "");
          setCompanyCity(companyData.city || "");
          setCompanyProvinceState(companyData.province_state || "");
          setCompanyPostalCode(companyData.postal_code || "");
          setCompanyPhoneNumber(companyData.company_phone_number || "");
          setLogoUrl(companyData.logo_url || null);
          setIsVatRegistered(companyData.is_vat_registered || false);
          setVatNumber(companyData.vat_number || "");
          setVatRate(companyData["VAT Rate"] || 15);
          
          // Banking information
          setUseBankInfo(companyData["Use Bank Info"] || false);
          setBankName(companyData["Bank Name"] || "");
          setBranchName(companyData["Branch Name"] || "");
          setBranchCode(companyData["Branch Code"] || "");
          setAccountHolder(companyData["Account Holder"] || "");
          setAccountNumber(companyData["Account Number"] || "");
        }
      }
    } catch (error) {
      console.error('Error fetching company data:', error);
      notify.error("Failed to load company data");
    }
  };

  useEffect(() => {
    fetchCompanyData();
  }, [user?.id]);

  const handleSaveCompany = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!guardWrite('edit company settings')) return;
    
    if (!user?.id) {
      notify.error("You must be logged in to update company details.");
      return;
    }

    if (!isCompanyAdmin) {
      notify.error("Only company admins can update company information.");
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (profileError) throw profileError;
      
      if (!profileData?.company_id) {
        throw new Error('No company found for this user');
      }

      const updateData = {
        name: companyName,
        email: email,
        website: website,
        street_address: companyStreetAddress,
        suburb: companySuburb,
        city: companyCity,
        province_state: companyProvinceState,
        postal_code: companyPostalCode,
        company_phone_number: companyPhoneNumber,
        logo_url: logoUrl,
        is_vat_registered: isVatRegistered,
        vat_number: isVatRegistered ? vatNumber : null,
        "VAT Rate": isVatRegistered ? vatRate : 15,
        "Use Bank Info": useBankInfo,
        "Bank Name": useBankInfo ? bankName : null,
        "Branch Name": useBankInfo ? branchName : null,
        "Branch Code": useBankInfo ? branchCode : null,
        "Account Holder": useBankInfo ? accountHolder : null,
        "Account Number": useBankInfo ? accountNumber : null,
        updated_at: new Date().toISOString()
      };

      const { data: updateResult, error: companyError } = await supabase
        .from('companies')
        .update(updateData)
        .eq("id", profileData.company_id)
        .select();
      
      if (companyError) throw companyError;
      
      if (!updateResult || updateResult.length === 0) {
        throw new Error('Update permission denied - this might be a Row Level Security issue');
      }
      
      notify.success("Your company information has been updated successfully.");

      await fetchCompanyData();
      
    } catch (error: any) {
      console.error('Company update failed:', error);
      notify.error(error.message || "An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto py-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Company Settings</h1>
        <p className="text-muted-foreground">Manage your company information and branding</p>
      </div>

      <Card>
        <form onSubmit={handleSaveCompany}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Company Information
              {!isCompanyAdmin && (
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <Lock className="h-4 w-4" />
                  View Only
                </div>
              )}
            </CardTitle>
            <CardDescription>
              {isCompanyAdmin 
                ? "Update your company details and branding. Use the controls below to position and crop your logo."
                : "Company information is read-only. Only company admins can make changes."
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex justify-center mb-4">
              <AdvancedLogoUpload 
                onImageUpload={setLogoUrl}
                initialImage={logoUrl || undefined}
                disabled={!isCompanyAdmin}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="companyName" className="flex items-center gap-1">
                <Building className="h-4 w-4" />
                Company Name
              </Label>
              <Input
                id="companyName"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                required
                disabled={!isCompanyAdmin}
                className={!isCompanyAdmin ? "bg-muted text-muted-foreground" : ""}
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-1">
                  <Mail className="h-4 w-4" />
                  Company Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={!isCompanyAdmin}
                  className={!isCompanyAdmin ? "bg-muted text-muted-foreground" : ""}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="companyPhoneNumber" className="flex items-center gap-1">
                  <Phone className="h-4 w-4" />
                  Company Phone
                </Label>
                <Input
                  id="companyPhoneNumber"
                  type="tel"
                  value={companyPhoneNumber}
                  onChange={(e) => setCompanyPhoneNumber(e.target.value)}
                  disabled={!isCompanyAdmin}
                  className={!isCompanyAdmin ? "bg-muted text-muted-foreground" : ""}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="website" className="flex items-center gap-1">
                <Globe className="h-4 w-4" />
                Website
              </Label>
              <Input
                id="website"
                type="text"
                placeholder="www.example.com"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                disabled={!isCompanyAdmin}
                className={!isCompanyAdmin ? "bg-muted text-muted-foreground" : ""}
              />
            </div>
            
            <div className="space-y-4">
              <Label className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                Company Address
              </Label>
              
              <div className="space-y-4">
                <Input
                  placeholder="Street Address"
                  value={companyStreetAddress}
                  onChange={(e) => setCompanyStreetAddress(e.target.value)}
                  disabled={!isCompanyAdmin}
                  className={!isCompanyAdmin ? "bg-muted text-muted-foreground" : ""}
                />
                
                <Input
                  placeholder="Suburb"
                  value={companySuburb}
                  onChange={(e) => setCompanySuburb(e.target.value)}
                  disabled={!isCompanyAdmin}
                  className={!isCompanyAdmin ? "bg-muted text-muted-foreground" : ""}
                />
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input
                    placeholder="City"
                    value={companyCity}
                    onChange={(e) => setCompanyCity(e.target.value)}
                    disabled={!isCompanyAdmin}
                    className={!isCompanyAdmin ? "bg-muted text-muted-foreground" : ""}
                  />
                  
                  <Input
                    placeholder="Province/State"
                    value={companyProvinceState}
                    onChange={(e) => setCompanyProvinceState(e.target.value)}
                    disabled={!isCompanyAdmin}
                    className={!isCompanyAdmin ? "bg-muted text-muted-foreground" : ""}
                  />
                </div>
                
                <Input
                  placeholder="Postal Code"
                  value={companyPostalCode}
                  onChange={(e) => setCompanyPostalCode(e.target.value)}
                  disabled={!isCompanyAdmin}
                  className={!isCompanyAdmin ? "bg-muted text-muted-foreground" : ""}
                />
              </div>
            </div>
            
            {/* VAT Registration Settings */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="vatRegistered"
                  checked={isVatRegistered}
                  onCheckedChange={setIsVatRegistered}
                  disabled={!isCompanyAdmin}
                />
                <Label htmlFor="vatRegistered">VAT Registered</Label>
              </div>
              
              {isVatRegistered && (
                <div className="space-y-4 pl-6">
                  <div className="space-y-2">
                    <Label htmlFor="vatNumber">VAT Number</Label>
                    <Input
                      id="vatNumber"
                      value={vatNumber}
                      onChange={e => setVatNumber(e.target.value)}
                      placeholder="Enter VAT number"
                      disabled={!isCompanyAdmin}
                      className={!isCompanyAdmin ? "bg-muted text-muted-foreground" : ""}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="vatRate">VAT Rate (%)</Label>
                    <Input
                      id="vatRate"
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={vatRate ?? ''}
                      onChange={e => {
                        const val = e.target.value;
                        setVatRate(val === '' ? ('' as any) : parseFloat(val));
                      }}
                      onBlur={e => {
                        const val = parseFloat(e.target.value);
                        if (isNaN(val) || e.target.value === '') {
                          setVatRate(15);
                        }
                      }}
                      placeholder="Enter VAT rate percentage"
                      disabled={!isCompanyAdmin}
                      className={!isCompanyAdmin ? "bg-muted text-muted-foreground" : ""}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Banking Information Section */}
            <div className="space-y-4 border-t pt-6">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Banking Information
                </Label>
                
                <div className="flex items-center space-x-2">
                  <Switch
                    id="useBankInfo"
                    checked={useBankInfo}
                    onCheckedChange={setUseBankInfo}
                    disabled={!isCompanyAdmin}
                  />
                  <Label htmlFor="useBankInfo" className="text-sm">
                    Include banking information on invoices
                  </Label>
                </div>
              </div>

              {useBankInfo && (
                <div className="space-y-4 pl-6 border-l-2 border-muted">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="bankName">Bank Name</Label>
                      <Input
                        id="bankName"
                        value={bankName}
                        onChange={e => setBankName(e.target.value)}
                        placeholder="e.g. First National Bank"
                        disabled={!isCompanyAdmin}
                        className={!isCompanyAdmin ? "bg-muted text-muted-foreground" : ""}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="branchName">Branch Name</Label>
                      <Input
                        id="branchName"
                        value={branchName}
                        onChange={e => setBranchName(e.target.value)}
                        placeholder="e.g. Main Branch"
                        disabled={!isCompanyAdmin}
                        className={!isCompanyAdmin ? "bg-muted text-muted-foreground" : ""}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="branchCode">Branch Code</Label>
                      <Input
                        id="branchCode"
                        value={branchCode}
                        onChange={e => setBranchCode(e.target.value)}
                        placeholder="e.g. 250655"
                        disabled={!isCompanyAdmin}
                        className={!isCompanyAdmin ? "bg-muted text-muted-foreground" : ""}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="accountHolder">Account Holder</Label>
                      <Input
                        id="accountHolder"
                        value={accountHolder}
                        onChange={e => setAccountHolder(e.target.value)}
                        placeholder="Account holder name"
                        disabled={!isCompanyAdmin}
                        className={!isCompanyAdmin ? "bg-muted text-muted-foreground" : ""}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="accountNumber">Account Number</Label>
                    <Input
                      id="accountNumber"
                      value={accountNumber}
                      onChange={e => setAccountNumber(e.target.value)}
                      placeholder="Bank account number"
                      disabled={!isCompanyAdmin}
                      className={!isCompanyAdmin ? "bg-muted text-muted-foreground" : ""}
                    />
                  </div>
                </div>
              )}
            </div>
          </CardContent>
          <CardFooter>
            <Button type="submit" disabled={isLoading || !isCompanyAdmin}>
              {isLoading ? "Saving..." : "Save Changes"}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default CompanySettings;
